import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { baseKeymap, toggleMark } from 'prosemirror-commands';
import { history, redo, undo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { liftListItem, sinkListItem, splitListItem } from 'prosemirror-schema-list';
import { EditorState, Selection, TextSelection, type Command, type Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { isPressed } from '../shortcuts.ts';
import { openThesaurus } from '../thesaurus/client.ts';
import { WritingMenu, targetFor, wordAt, type MenuAt } from './writingAids.tsx';
import { syncSelection } from './selection.ts';
import { checkEditor, clearSpelling, misspellingAt, misspellings, spellingPlugin, type ProofingScope } from './spellingPlugin.ts';
import { useSettings } from '../settings.ts';
import { useSpellingVersion } from '../spelling/client.ts';
import { entryIdOfLink } from '../security/sanitize.ts';
import {
  align,
  alignmentOf,
  blockStyleOf,
  bulletList,
  clearFormatting,
  inLink,
  indent,
  isBold,
  isItalic,
  isStrike,
  isUnderline,
  linkable,
  numberedList,
  outdent,
  removeLink,
  setBlockStyle,
  setLink,
  toggleBold,
  toggleItalic,
  toggleStrike,
  toggleUnderline,
  type Alignment,
  type BlockStyle,
} from './commands.ts';
import { FormatControls } from './FormatControls.tsx';
import { docFromHtml, htmlFromDoc, sliceFromHtml } from './html.ts';
import { chooseMatch, mentionPicker, openPicker, selectMatch, startMention, type MentionCandidate, type OpenPicker } from './mentionPicker.ts';
import { entrySchema } from './schema.ts';

const { marks, nodes } = entrySchema;

function editingKeys(): Plugin[] {
  return [
    history(),
    keymap({
      'Mod-z': undo,
      'Shift-Mod-z': redo,
      'Mod-y': redo,
      'Mod-b': toggleMark(marks['bold']!),
      'Mod-i': toggleMark(marks['italic']!),
      'Mod-u': toggleMark(marks['underline']!),
      Enter: splitListItem(nodes['list_item']!),
      'Mod-[': liftListItem(nodes['list_item']!),
      'Mod-]': sinkListItem(nodes['list_item']!),
      'Shift-Enter': (state, dispatch) => {
        dispatch?.(state.tr.replaceSelectionWith(nodes['hard_break']!.create()).scrollIntoView());
        return true;
      },
    }),
    keymap(baseKeymap),
  ];
}

/**
 * Pasted and dropped HTML goes through DOMPurify into the schema (editor/html.ts), never
 * through ProseMirror's own clipboard parser, which writes the HTML with `innerHTML` and
 * so is refused under the app's Trusted Types policy. Plain text, and drags within the
 * editor, are left to ProseMirror: they involve no HTML.
 */
function takeHtml(view: EditorView, html: string, at?: number): void {
  const slice = sliceFromHtml(html);
  const tr = at === undefined ? view.state.tr.replaceSelection(slice) : view.state.tr.replaceRange(at, at, slice);
  view.dispatch(tr.scrollIntoView().setMeta('uiEvent', at === undefined ? 'paste' : 'drop'));
}

export interface RichTextEditorProps {
  /** The entry's stored HTML. Read once, when the editor is made. */
  readonly initialHtml: string;
  readonly onChange: (html: string) => void;
  /** Ctrl+click (Cmd+click on a Mac) on a mention link, as on the desktop. */
  readonly onOpenEntry?: (entryId: string) => void;
  readonly label: string;
  /** The entries the `[[` picker offers (docs/ENTRY-EDITOR-SLICE-2-PLAN.md section 4.5). */
  readonly mentionCandidates?: readonly MentionCandidate[];
  /** False for focus mode's bare page: the formatting keys still work. */
  readonly toolbar?: boolean;
  /** Puts the cursor at the end of the text once the editor is made. */
  readonly autoFocus?: boolean;
  /** Spelling checked as you write, remembered for this scene or entry (docs/PARITY-STEP-6-PLAN.md section 6). */
  readonly proofing?: ProofingScope | undefined;
}

/** The rich-text editor with its toolbar (docs/ENTRY-EDITOR-PLAN.md sections 4.1 and 4.5). */
export function RichTextEditor({ initialHtml, onChange, onOpenEntry, label, mentionCandidates, toolbar = true, autoFocus = false, proofing }: RichTextEditorProps) {
  const host = useRef<HTMLDivElement>(null);
  const settings = useSettings();
  const spellingOn = proofing !== undefined && settings['writing.spelling'];
  const dialect = settings['writing.dialect'];
  const spellingVersion = useSpellingVersion();
  /** Checks a moment after typing stops, as the desktop does. */
  const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [note, setNote] = useState('');
  const latest = useRef({ onChange, onOpenEntry, mentionCandidates, proofing, spellingOn, dialect, scheduleCheck: () => {} });
  latest.current = { ...latest.current, onChange, onOpenEntry, mentionCandidates, proofing, spellingOn, dialect };
  const listId = useId();
  const [view, setView] = useState<EditorView | null>(null);
  const [, setState] = useState<EditorState | null>(null);
  /** The right-click menu on a word (editor/writingAids.tsx). */
  const [menu, setMenu] = useState<MenuAt | null>(null);
  // The mention list is placed by the text's position on screen: place it again on a resize.
  const [, setLayout] = useState(0);
  useEffect(() => {
    const relayout = () => setLayout((n) => n + 1);
    window.addEventListener('resize', relayout);
    return () => window.removeEventListener('resize', relayout);
  }, []);

  useEffect(() => {
    const editor = new EditorView(host.current!, {
      // The picker comes first, so its keys (Enter, Tab, the arrows) win while its list is open.
      state: EditorState.create({
        doc: docFromHtml(initialHtml),
        plugins: [mentionPicker(() => latest.current.mentionCandidates ?? []), ...editingKeys(), spellingPlugin()],
      }),
      attributes: (state) => {
        const open = openPicker(state);
        const picking: Record<string, string> = open
          ? {
              'aria-autocomplete': 'list',
              'aria-controls': listId,
              ...(open.matches.length > 0 ? { 'aria-activedescendant': `${listId}-${open.selected}` } : {}),
            }
          : {};
        // With Realmery's own checking on, the browser's is off, so nothing is underlined twice.
        return { class: 'rich-text', role: 'textbox', 'aria-multiline': 'true', 'aria-label': label, spellcheck: latest.current.spellingOn ? 'false' : 'true', ...picking };
      },
      dispatchTransaction(tr) {
        editor.updateState(editor.state.apply(tr));
        setState(editor.state); // the toolbar follows the cursor
        if (tr.docChanged) {
          latest.current.onChange(htmlFromDoc(editor.state.doc));
          latest.current.scheduleCheck();
        }
      },
      // Shift+F7 (writing.thesaurus): the Thesaurus on the word at the caret, or the phrase selected.
      handleKeyDown(v, event) {
        // F7 (writing.check): the next misspelling after the caret, with its menu.
        if (isPressed(event, 'writing.check')) {
          event.preventDefault();
          syncSelection(v);
          const { proofing: scope, spellingOn: on } = latest.current;
          if (!scope) return true;
          if (!on) {
            setNote('Spelling is off: Settings > Writing turns it on.');
            return true;
          }
          // The one the caret is in, else the next; after one is selected (a previous F7), the one after it.
          const found = misspellings(v.state);
          const { selection } = v.state;
          const next = (selection.empty ? found.find((m) => m.to >= selection.from) : found.find((m) => m.from >= selection.to)) ?? found[0];
          if (!next) {
            setNote('No misspellings found.');
            return true;
          }
          v.dispatch(v.state.tr.setSelection(TextSelection.create(v.state.doc, next.from, next.to)).scrollIntoView());
          const at = v.coordsAtPos(next.from);
          const spot = wordAt(v.state, next.from + 1);
          if (spot) setMenu({ x: at.left, y: at.bottom + 4, spot, misspelling: next });
          return true;
        }
        if (!isPressed(event, 'writing.thesaurus')) return false;
        event.preventDefault();
        syncSelection(v);
        const spot = wordAt(v.state);
        openThesaurus(spot ? targetFor(v, spot) : null);
        return true;
      },
      handleDOMEvents: {
        // Leaving for the toolbar: the selection as seen, which the editor may not have read yet
        // (it follows the browser's on its next selectionchange). Once focus has moved, it's gone.
        blur(v) {
          syncSelection(v);
          return false;
        },
        // A right-click on a word: its synonyms. Shift+right-click keeps the browser's own menu.
        contextmenu(v, event) {
          if (event.shiftKey) return false;
          const at = v.posAtCoords({ left: event.clientX, top: event.clientY });
          const spot = at ? wordAt(v.state, at.pos) : null;
          if (!spot) return false;
          const misspelling = misspellingAt(v.state, at!.pos);
          event.preventDefault();
          // The word is selected, as a right-click selects it, so Cut and Copy act on it.
          const sel = v.state.selection;
          if (sel.empty || spot.from < sel.from || spot.to > sel.to) v.dispatch(v.state.tr.setSelection(TextSelection.create(v.state.doc, spot.from, spot.to)));
          setMenu({ x: event.clientX, y: event.clientY, spot, misspelling });
          return true;
        },
        paste(v, event) {
          const html = event.clipboardData?.getData('text/html');
          if (!html) return false;
          event.preventDefault();
          takeHtml(v, html);
          return true;
        },
        drop(v, event) {
          if (v.dragging) return false;
          const html = event.dataTransfer?.getData('text/html');
          if (!html) return false;
          event.preventDefault();
          const at = v.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
          takeHtml(v, html, at ?? v.state.selection.from);
          return true;
        },
        click(_v, event) {
          if (!(event.ctrlKey || event.metaKey)) return false;
          const link = (event.target as Element | null)?.closest?.('a');
          const id = link ? entryIdOfLink(link.getAttribute('href') ?? '') : null;
          if (!id || !latest.current.onOpenEntry) return false;
          event.preventDefault();
          latest.current.onOpenEntry(id);
          return true;
        },
      },
    });
    setView(editor);
    setState(editor.state);
    if (autoFocus) {
      editor.dispatch(editor.state.tr.setSelection(Selection.atEnd(editor.state.doc)).scrollIntoView());
      editor.focus();
    }
    return () => {
      editor.destroy();
      setView(null);
    };
    // The editor owns its document once made; a new entry gets a new editor (a new key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Spelling: checked when the editor opens, a moment after each change, and whenever a setting, a dictionary or an ignore changes.
  useEffect(() => {
    if (!view) return;
    const run = () => {
      const { proofing: scope, spellingOn: on, dialect: d } = latest.current;
      if (!scope || !on) return;
      void checkEditor(view, scope, d).catch(() => setNote("Spelling couldn't be checked: the dictionary couldn't be loaded."));
    };
    latest.current.scheduleCheck = () => {
      if (checkTimer.current) clearTimeout(checkTimer.current);
      checkTimer.current = setTimeout(run, 800);
    };
    if (spellingOn) run();
    else clearSpelling(view);
    // The browser's own checking follows the setting.
    view.setProps({});
    return () => {
      if (checkTimer.current) clearTimeout(checkTimer.current);
    };
  }, [view, spellingOn, dialect, spellingVersion]);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(''), 4000);
    return () => clearTimeout(t);
  }, [note]);

  return (
    <div className="rich-text-editor">
      {view && toolbar && <Toolbar view={view} mentions={mentionCandidates !== undefined} />}
      <div ref={host} className="rich-text-host" />
      {view && <MentionList view={view} listId={listId} open={openPicker(view.state)} />}
      {view && menu && <WritingMenu view={view} at={menu} proofing={proofing} onClose={() => setMenu(null)} />}
      <p className="visually-hidden" role="status">
        {note}
      </p>
      {note && <p className="editor-note meta">{note}</p>}
    </div>
  );
}

const STYLES: readonly [BlockStyle, string][] = [
  ['paragraph', 'Paragraph'],
  ['h1', 'Heading 1'],
  ['h2', 'Heading 2'],
  ['h3', 'Heading 3'],
  ['h4', 'Heading 4'],
  ['h5', 'Heading 5'],
  ['h6', 'Heading 6'],
];

const ALIGNMENTS: readonly [Alignment, string][] = [
  ['left', 'Left'],
  ['center', 'Centre'],
  ['right', 'Right'],
  ['justify', 'Justify'],
];

function Toolbar({ view, mentions }: { view: EditorView; mentions: boolean }) {
  const state = view.state;
  const [linking, setLinking] = useState(false);
  const run = (command: Command) => {
    syncSelection(view);
    command(view.state, view.dispatch);
    view.focus();
  };
  const style = blockStyleOf(state);
  const alignment = alignmentOf(state);

  return (
    <div className="editor-toolbar" role="toolbar" aria-label="Formatting">
      <select
        aria-label="Paragraph style"
        value={style ?? ''}
        onChange={(event) => {
          if (event.currentTarget.value) run(setBlockStyle(event.currentTarget.value as BlockStyle));
        }}
      >
        {style === null && <option value="">Mixed</option>}
        {STYLES.map(([value, name]) => (
          <option key={value} value={value}>
            {name}
          </option>
        ))}
      </select>
      <FormatControls view={view} />
      <span className="toolbar-group">
        <ToolButton label="Bold (Ctrl+B)" pressed={isBold(state)} onRun={() => run(toggleBold)}>
          <strong>B</strong>
        </ToolButton>
        <ToolButton label="Italic (Ctrl+I)" pressed={isItalic(state)} onRun={() => run(toggleItalic)}>
          <em>I</em>
        </ToolButton>
        <ToolButton label="Underline (Ctrl+U)" pressed={isUnderline(state)} onRun={() => run(toggleUnderline)}>
          <u>U</u>
        </ToolButton>
        <ToolButton label="Strikethrough" pressed={isStrike(state)} onRun={() => run(toggleStrike)}>
          <s>S</s>
        </ToolButton>
      </span>
      <span className="toolbar-group">
        <ToolButton label="Bulleted list" onRun={() => run(bulletList)}>
          • List
        </ToolButton>
        <ToolButton label="Numbered list" onRun={() => run(numberedList)}>
          1. List
        </ToolButton>
        <ToolButton label="Outdent" onRun={() => run(outdent)}>
          ⇤
        </ToolButton>
        <ToolButton label="Indent" onRun={() => run(indent)}>
          ⇥
        </ToolButton>
      </span>
      <span className="toolbar-group">
        {ALIGNMENTS.map(([value, name]) => (
          <ToolButton key={value} label={`Align ${name.toLowerCase()}`} pressed={alignment === value} onRun={() => run(align(value))}>
            {name}
          </ToolButton>
        ))}
      </span>
      <span className="toolbar-group">
        {inLink(state) ? (
          <ToolButton label="Remove link" onRun={() => run(removeLink)}>
            Unlink
          </ToolButton>
        ) : (
          <ToolButton label="Link the selected text" disabled={state.selection.empty} onRun={() => setLinking(true)}>
            Link
          </ToolButton>
        )}
        <ToolButton label="Clear formatting" disabled={state.selection.empty} onRun={() => run(clearFormatting)}>
          Clear
        </ToolButton>
        {mentions && (
          <ToolButton label="Mention an entry (or type [[)" onRun={() => startMention(view)}>
            [[ Mention
          </ToolButton>
        )}
      </span>
      {linking && (
        <LinkForm
          onDone={(href) => {
            setLinking(false);
            if (href) run(setLink(href));
            else view.focus();
          }}
        />
      )}
    </div>
  );
}

/** A toolbar button that keeps the editor's selection: the mouse press does not take focus. */
function ToolButton({ label, pressed, disabled, onRun, children }: {
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  onRun: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="tool"
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onRun}
    >
      {children}
    </button>
  );
}

function LinkForm({ onDone }: { onDone: (href: string | null) => void }) {
  const [href, setHref] = useState('https://');
  const valid = linkable(href);
  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid) onDone(href);
  }
  return (
    <form className="link-form" onSubmit={submit}>
      <label>
        Link address
        <input
          type="url"
          value={href}
          autoFocus
          onChange={(event) => setHref(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onDone(null);
          }}
        />
      </label>
      <button type="submit" className="primary" disabled={!valid}>
        Add link
      </button>
      <button type="button" className="quiet" onClick={() => onDone(null)}>
        Cancel
      </button>
      {!valid && href.trim() !== '' && href !== 'https://' && <p className="meta">Use a web (https://) or email (mailto:) address.</p>}
    </form>
  );
}

const PICKER_WIDTH = 288;

/**
 * The picker's list, below the `[[`. The editor keeps the keyboard focus and names the
 * highlighted option with aria-activedescendant, so screen readers announce each choice.
 */
function MentionList({ view, listId, open }: { view: EditorView; listId: string; open: OpenPicker | null }) {
  if (!open) return null;
  const box = view.dom.parentElement!.parentElement!.getBoundingClientRect();
  const at = view.coordsAtPos(open.from);
  const left = Math.max(0, Math.min(at.left - box.left, box.width - PICKER_WIDTH));
  const count = open.matches.length;
  const query = open.query.trim();
  return (
    <div className="mention-picker" style={{ left, top: at.bottom - box.top + 4 }}>
      <p className="visually-hidden" role="status" aria-live="polite">
        {count === 0
          ? 'No entry matches.'
          : `${count} ${count === 1 ? 'entry matches' : 'entries match'}. Up and down to choose, Enter to insert, Escape to close.`}
      </p>
      {count === 0 ? (
        <p className="mention-empty">{query ? `No entry is called “${query}”.` : 'There are no other entries yet.'}</p>
      ) : (
        <ul id={listId} role="listbox" aria-label="Entries to mention">
          {open.matches.map((match, index) => (
            <li
              key={match.entry.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === open.selected}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => selectMatch(view, index)}
              onClick={() => chooseMatch(view, index)}
            >
              <span className="mention-name">{match.text}</span>
              <span className="meta">
                {match.text !== match.entry.title ? `${match.entry.title} · ` : ''}
                {match.entry.typeName}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
